import toast from 'react-hot-toast';
import { downloadPdfApi, postBusinessInvoiceApi, putBusiness, putBusinessInvoiceApi } from '~/api';
import { createAppAsyncThunk } from '../../createAppAsyncThunk';
import { serializeAndValidateFormData } from './helpers';
import { ApiResponseEnum, apiResponseHandler } from '~/utils/api';
import { formSlice } from '.';
import { INVOICE_LIST_ROUTE } from '~/constants/routes';
import { clearUserLimits } from '../userLimits';

interface SubmitFormResponseType {
  uuid: string;
}

interface PdfGenResponseType {
  pdf_url: string;
}

// Define the async thunk for submitting form data
export const submitFormData = createAppAsyncThunk(
  'form/submitFormData',
  async (_, { getState, dispatch }) => {
    const state = getState();
    const { formData, isValid, errorMsg } = serializeAndValidateFormData(state.form.formData);

    if (!isValid) {
      toast.error(errorMsg || 'Please fill required details');
    }

    const storeInvoiceUuid = state.form.invoiceId;
    const invoiceUuid = formData.invoiceUuid || storeInvoiceUuid;

    try {
      const businessUuid = state.user.businessDetails.business_uuid;

      const promise = invoiceUuid
        ? await putBusinessInvoiceApi(businessUuid, invoiceUuid, formData)
        : await postBusinessInvoiceApi(businessUuid, formData);

      const response = await apiResponseHandler<SubmitFormResponseType>(promise);
      let uuid = '';

      if (response.type === ApiResponseEnum.Success) {
        uuid = response.data.uuid;
      } else {
        toast.error(
          response.error ?? 'An error occurred while saving your data, please try again later',
        );
        return;
      }
      dispatch(formSlice.actions.setInvoiceId(uuid));

      // Invalidate user limits cache on new invoice creation
      if (!invoiceUuid) {
        dispatch(clearUserLimits());
      }

      const { saveTermsAsDefault, saveNotesAsDefault } = state.form.formData;
      if (saveTermsAsDefault || saveNotesAsDefault) {
        const business = state.user.businessDetails;
        await putBusiness(business.business_uuid, {
          ...business,
          ...(saveTermsAsDefault && { terms: state.form.formData.terms }),
          ...(saveNotesAsDefault && { notes: state.form.formData.notes }),
        });
      }
    } catch (error) {
      toast.error('An error occurred while submitting your data, please try again later');
      console.error('Error submitting form data:', error);
    }
  },
);

export const submitFormDataAndDownloadPdf = createAppAsyncThunk(
  'form/submitFormDataAndDownloadPdf',
  async (_, { getState, dispatch }) => {
    const state = getState();
    const { formData, isValid, errorMsg } = serializeAndValidateFormData(state.form.formData);

    if (!isValid) {
      toast.error(errorMsg || 'Please fill required details');
    }

    const storeInvoiceUuid = state.form.invoiceId;
    const invoiceUuid = formData.invoiceUuid || storeInvoiceUuid;

    try {
      const businessUuid = state.user.businessDetails.business_uuid;

      const submitPromise = invoiceUuid
        ? await putBusinessInvoiceApi(businessUuid, invoiceUuid, formData)
        : await postBusinessInvoiceApi(businessUuid, formData);

      const submitResponse = await apiResponseHandler<SubmitFormResponseType>(submitPromise);
      let invoiceId = '';

      if (submitResponse.type === ApiResponseEnum.Success) {
        invoiceId = submitResponse.data.uuid;
      } else {
        toast.error(
          submitResponse.error ??
            'An error occurred while saving your data, please try again later',
        );
        return;
      }

      // Invalidate user limits cache on new invoice creation
      if (!invoiceUuid) {
        dispatch(clearUserLimits());
      }

      const { saveTermsAsDefault, saveNotesAsDefault } = state.form.formData;
      if (saveTermsAsDefault || saveNotesAsDefault) {
        const business = state.user.businessDetails;
        await putBusiness(business.business_uuid, {
          ...business,
          ...(saveTermsAsDefault && { terms: state.form.formData.terms }),
          ...(saveNotesAsDefault && { notes: state.form.formData.notes }),
        });
      }

      const promise = await downloadPdfApi(invoiceId, {
        template_id: formData.template_id,
      });
      const response = await apiResponseHandler<PdfGenResponseType>(promise);

      if (response.type !== ApiResponseEnum.Success) {
        toast.error('An error occurred while generating your PDF, please try again later');
        return;
      }

      toast.success('Invoice saved successfully');
      window.location.href = `${INVOICE_LIST_ROUTE}?viewId=${invoiceId}`;
    } catch (error) {
      toast.error('An error occurred while submitting your data, please try again later');
      console.error('Error submitting form data:', error);
    }
  },
);

export const downloadInvoicePdf = createAppAsyncThunk(
  'form/downloadInvoicePdf',
  async ({ templateId }: { templateId: string }, { getState }) => {
    const state = getState();
    const invoiceId = state.form.invoiceId as string;
    try {
      // Make call for PDF
      const promise = await downloadPdfApi(invoiceId, {
        template_id: templateId,
      });
      const response = await apiResponseHandler<PdfGenResponseType>(promise);

      if (response.type !== ApiResponseEnum.Success) {
        toast.error('An error occurred while generating your PDF, please try again later');
        return;
      }

      toast.success('Invoice saved successfully');
      window.location.href = `${INVOICE_LIST_ROUTE}?viewId=${invoiceId}`;
    } catch (error) {
      toast.error('An error occurred while downloading your pdf, please try again later');
      console.error('Error submitting downloading pdf:', error);
    }
  },
);
